<template>
  <Card
    class="!p-4 gap-y-4 grid relative"
    :class="{ 'grid-cols-[1fr_2rem]': !hideOptions }"
  >
    <ContextMenuList
      v-if="!hideOptions"
      class="absolute top-4 right-4"
      :options="contextActions"
    >
      <template v-slot="{ isOpen, open, close }">
        <NamiButton
          text
          :icon="IconDots"
          button-type="secondary"
          small
          pill
          :disabled="deletePending"
          @button-click="() => (isOpen ? close() : open())"
        />
      </template>
    </ContextMenuList>

    <div class="flex flex-col gap-1">
      <h3 class="truncate">
        <TheNuxtLink :href="href.list.id(titleList.id)" :target>
          <span class="font-bold">{{ titleList.attributes.title }}</span>
        </TheNuxtLink>
        <span
          v-if="titleList.attributes.visibility === 'private'"
          class="uppercase text-xs font-bold bg-red-500 px-1.5 rounded-full text-white ml-2"
          >Private
        </span>
      </h3>
      <div
        v-if="creator && oauth?.profile.sub !== creator.id && !hideUser"
        class="flex items-center gap-2 truncate mb-1"
      >
        <UserAvatar :user="creator" :size="24" class="flex-shrink-0" />
        <span class="font-medium text-sm truncate min-w-0">{{
          creator.attributes?.displayName ?? creator.attributes?.username
        }}</span>
      </div>
      <div
        v-if="
          titleList.attributes.triggers.reading_status &&
          creator &&
          oauth?.profile.sub === creator.id
        "
        class="flex items-center gap-2"
      >
        <span
          v-if="titleList.attributes.triggers.reading_status"
          class="text-xs rounded-full bg-neutral-200 dark:bg-neutral-600 px-2 py-0.5 whitespace-nowrap"
        >
          {{
            $t(
              `entity.title.follow.reading_status.${titleList.attributes.triggers.reading_status}`,
            )
          }}
        </span>
        <IconBellRinging
          class="text-nami-comi-blue flex-shrink-0"
          v-if="titleList.attributes.triggers.notify"
          :size="16"
        />
      </div>
    </div>
    <div v-if="!hideOptions"></div>

    <template v-if="titleList.attributes.description">
      <p>
        {{ titleList.attributes.description }}
      </p>
      <div v-if="!hideOptions"></div>
    </template>

    <div
      v-if="titles.length > 0"
      class="h-32 lg:h-48 flex gap-2 col-span-full overflow-x-auto"
    >
      <TheNuxtLink
        v-for="title in titles"
        :href="linkTo(title)"
        :target
        class="aspect-[5/7]"
      >
        <AsyncImage
          :src="getCoverUrl(title, '128')"
          class="aspect-[5/7] rounded-md object-cover h-full"
        />
      </TheNuxtLink>
      <TheNuxtLink
        v-if="hiddenTitles"
        :href="href.list.id(titleList.id)"
        :target
      >
        <div
          class="aspect-[5/7] rounded-md h-full bg-neutral-200 hover:bg-neutral-100 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-center flex items-center justify-center font-bold transition-colors"
        >
          See All
        </div>
      </TheNuxtLink>
    </div>
    <NamiButton
      v-else-if="hiddenTitles"
      button-type="secondary"
      :href="linkTo(titleList)"
      class="col-span-full"
      :target
    >
      View All Titles
    </NamiButton>
    <div
      v-else
      class="text-center bg-neutral-200 dark:bg-neutral-700 py-2 rounded-md col-span-full"
    >
      This list has no titles.
    </div>
    <TitleListShareModal v-model="shareModalOpen" :titleList />
    <div
      v-if="deletePending"
      class="absolute inset-0 rounded-none sm:rounded-md flex items-center justify-center bg-neutral-100/40 dark:bg-neutral-900/40"
    >
      <NamiLoading size="small" />
    </div>
  </Card>
</template>

<script setup lang="ts">
import {
  isUser,
  type TitleEntity,
  type TitleListEntity,
  getCoverUrl,
  isTitle,
  TitleList,
} from "~/src/api";
import {
  IconBellRinging,
  IconCopy,
  IconDots,
  IconEyeOff,
  IconPencil,
  IconShare,
  IconTrash,
} from "@tabler/icons-vue";

const nuxtApp = useNuxtApp();
const appStore = nuxtApp.$app();

const props = defineProps<{
  titleList: TitleListEntity;
  // this is assumed to be titles in the list
  titles: TitleEntity[];
  hideUser?: boolean;
  hideOptions?: boolean;
  target?: "_blank" | "_self";
}>();

const emit = defineEmits<{
  (e: "deleted"): void;
}>();

const creator = computed(() => props.titleList.relationships.find(isUser));
const oauth = useLoggedOAuthUserData();
const loggedUserId = computed(() => oauth.value?.profile.sub ?? "");
const href = useHref();

const shareModalOpen = ref(false);

const contextActions = computed(() => {
  const base = [
    {
      name: "Clone",
      href: href.value.list.create + `?tl=${props.titleList.id}`,
      icon: IconCopy,
    },
    !!loggedUserId.value &&
      loggedUserId.value === creator.value?.id && {
        name: "Edit",
        href: href.value.list.edit(props.titleList.id),
        icon: IconPencil,
      },
    {
      name: "Share",
      icon: IconShare,
      action: () => {
        shareModalOpen.value = true;
      },
    },
    !!loggedUserId.value && {
      name: "Delete",
      icon: IconTrash,
      action: deleteList,
    },
  ];

  return base.filter(onlyTruthys);
});

// beyond 19 provided titles, we have no way of knowing if there are more titles in the list
const hiddenTitles = computed(
  () =>
    props.titleList.relationships.filter(isTitle).length - props.titles.length,
);

const { action: deleteList, pending: deletePending } = useAction2(async () => {
  let result = "cancel";
  if (appStore) {
    result = await appStore?.prompt(
      "Are you sure you want to delete this list?",
      {
        icon: "confused",
        buttons: {
          cancel: {
            buttonType: "secondary",
            buttonText: "Cancel",
          },
          confirm: {
            buttonType: "danger",
            buttonText: "Delete",
          },
        },
      },
    );
  } else if (confirm("Are you sure you want to delete this list?")) {
    result = "confirm";
  }
  if (result === "cancel") return;

  await executeWithNotificationOnError(async () => {
    await TitleList.delete(
      props.titleList.id,
      props.titleList.attributes.version,
      await getTokenOrThrow(),
    );
    appStore?.notify({
      preset: "success.plain",
      title: "List deleted.",
      timer: 3000,
    });
    emit("deleted");
  });
});
</script>
